import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { GridRow, Column, MoneyField } from '../common';
import { CreditCardTypePicker, DatePicker } from '../Pickers';
import { formatCurrency } from '../../helpers/util';

export const CreditCardPayment = ({
  cardLast4,
  cardType,
  actions,
  amount,
  amountErrorMessage,
  federalShareErrorMessage,
  nonFederalShareErrorMessage,
  amountRemaining,
  showAmountRemaining,
  federalShare,
  nonFederalShare,
  nonFederalShareLabel,
  splitAmounts,
  dateReceived,
  dateReceivedErrorMessage,
  description,
  referenceId,
  sourceCode,
  dateLabel,
  cardholderName,
  includeCardholderName,
  descriptionLabel,
  descriptionFieldName,
  mailCode,
  envelopeCode,
  externalDonorId,
}) => (
  <Fragment>
    <GridRow>
      <Column lg={6}>
        <TextField
          value={cardLast4}
          label="Credit Card Last 4 Digits"
          onChange={actions.handlePaymentInfoChange('cardLast4')}
        />
      </Column>
      <Column lg={6}>
        <CreditCardTypePicker
          selectedKey={cardType || 0}
          onChange={actions.handlePaymentInfoChange('cardType')}
        />
      </Column>
    </GridRow>
    <GridRow>
      {includeCardholderName && (
        <Column lg={6}>
          <TextField
            value={cardholderName}
            label="Cardholder Name"
            onChange={actions.handlePaymentInfoChange('cardholderName')}
          />
        </Column>
      )}
      <Column lg={includeCardholderName ? 6 : 12}>
        <TextField
          value={description}
          label={descriptionLabel}
          onChange={actions.handlePaymentInfoChange(descriptionFieldName)}
          spellCheck={true}
        />
      </Column>
    </GridRow>
    {!splitAmounts &&
    <GridRow>
      <Column lg={3}>
        <MoneyField
          label="Amount"
          value={amount}
          required
          errorMessage={amountErrorMessage}
          onChange={actions.handlePaymentInfoChange('amount')}
          description={
            showAmountRemaining
              ? `Remaining to match end recipients total: ${formatCurrency(
                  amountRemaining,
                )}`
              : ''
          }
        />
      </Column>
      <Column lg={3}>
        <DatePicker
          label={dateLabel}
          value={dateReceived}
          onChange={actions.handleChangeDate}
          required
          errorMessage={dateReceivedErrorMessage}
        />
      </Column>
      <Column lg={3}>
        <TextField
          label="Reference ID"
          value={referenceId}
          onChange={actions.handlePaymentInfoChange('referenceId')}
        />
      </Column>
      <Column lg={3}>
        <TextField
          label="Source Code"
          value={sourceCode}
          onChange={actions.handlePaymentInfoChange('sourceCode')}
        />
      </Column>
    </GridRow>
    }
    {!splitAmounts &&
      <GridRow>
        <Column lg={3}>
          <TextField
            label="Mail Code"
            value={mailCode || ''}
            onChange={actions.handlePaymentInfoChange('mailCode')}
          />
        </Column>
        <Column lg={3}>
          <TextField
            label="Envelope Code"
            value={envelopeCode || ''}
            onChange={actions.handlePaymentInfoChange('envelopeCode')}
          />
        </Column>
        <Column lg={3}>
          <TextField
            label="Donor ID"
            value={externalDonorId || ''}
            onChange={actions.handlePaymentInfoChange('externalDonorId')}
          />
        </Column>
      </GridRow>
    }
    {splitAmounts &&
    <GridRow>
      <Column lg={2}>
        <MoneyField
          label="Federal Share"
          value={federalShare}
          required
          errorMessage={federalShareErrorMessage}
          onChange={actions.handlePaymentInfoChange('federalShare')}
        />
      </Column>
      <Column lg={2}>
        <MoneyField
          label={nonFederalShareLabel}
          value={nonFederalShare}
          required
          errorMessage={nonFederalShareErrorMessage}
          onChange={actions.handlePaymentInfoChange('nonFederalShare')}
        />
      </Column>
      <Column lg={2}>
        <DatePicker
          label={dateLabel}
          value={dateReceived}
          onChange={actions.handleChangeDate}
          required
          errorMessage={dateReceivedErrorMessage}
        />
      </Column>
      <Column lg={3}>
        <TextField
          label="Reference ID"
          value={referenceId}
          onChange={actions.handlePaymentInfoChange('referenceId')}
        />
      </Column>
      <Column lg={3}>
        <TextField
          label="Source Code"
          value={sourceCode}
          onChange={actions.handlePaymentInfoChange('sourceCode')}
        />
      </Column>
    </GridRow>
    }
  </Fragment>
);

CreditCardPayment.propTypes = {
  actions: PropTypes.object.isRequired,
  cardLast4: PropTypes.string,
  cardType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  amountErrorMessage: PropTypes.string,
  federalShareErrorMessage: PropTypes.string,
  nonFederalShareErrorMessage: PropTypes.string,
  amountRemaining: PropTypes.number,
  showAmountRemaining: PropTypes.bool,
  splitAmounts: PropTypes.bool,
  federalShare: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nonFederalShare: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  nonFederalShareLabel: PropTypes.string,
  dateReceived: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.number,
  ]),
  dateReceivedErrorMessage: PropTypes.string,
  referenceId: PropTypes.string,
  sourceCode: PropTypes.string,
  description: PropTypes.string,
  dateLabel: PropTypes.string,
  descriptionLabel: PropTypes.string,
  descriptionFieldName: PropTypes.string,
  cardholderName: PropTypes.string,
  includeCardholderName: PropTypes.bool,
  mailCode: PropTypes.string,
  envelopeCode: PropTypes.string,
  externalDonorId: PropTypes.string,
};

CreditCardPayment.defaultProps = {
  cardLast4: '',
  cardType: 0,
  amount: '',
  amountErrorMessage: '',
  federalShareErrorMessage: '',
  nonFederalShareErrorMessage: '',
  amountRemaining: 0,
  showAmountRemaining: false,
  splitAmounts: false,
  federalShare: '',
  nonFederalShare: '',
  nonFederalShareLabel: 'Non-Federal Share',
  dateReceived: new Date(),
  dateReceivedErrorMessage: '',
  referenceId: '',
  description: '',
  dateLabel: 'Date Received',
  descriptionLabel: 'Description',
  descriptionFieldName: 'description',
  cardholderName: '',
  includeCardholderName: true,
  mailCode: '',
  envelopeCode: '',
  externalDonorId: '',
};

export default CreditCardPayment;
